import React, { useMemo } from 'react'
import moment from 'moment-business-days'
import styles from './styles.module.scss'
import LoadingSpinner from 'components/public/landing/loadingSpinner/LoadingSpinner'
import { Absence, AbsenceReason } from 'Types/Absences/Absence'
import { Filter } from 'Types/Filter'
import { IHoliday } from 'Types/Holidays/IHoliday'
import {
  TIME_OFF_REASONS,
  clipToMonth,
  countWorkingDays,
  getMonthRange,
  isTimeOff,
  toHolidayDates,
} from '../utils/absenceDays'

interface IProps {
  absences: Absence[]
  holidays: IHoliday[] | undefined
  filters: Filter[]
  loading: boolean
}

const formatDays = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`

const formatRange = (absence: Absence) => {
  const from = moment(absence.absenceFrom)
  const to = moment(absence.absenceTo)
  return from.isSame(to, 'day')
    ? from.format('DD.MM.')
    : `${from.format('DD.MM.')} – ${to.format('DD.MM.')}`
}

const TimeOff = ({ absences, holidays, filters, loading }: IProps) => {
  const { monthStart, monthEnd } = useMemo(() => getMonthRange(filters), [filters])

  const entries = useMemo(() => {
    const holidayDates = toHolidayDates(holidays)

    return absences
      .filter(isTimeOff)
      .sort((a, b) => moment(a.absenceFrom).diff(moment(b.absenceFrom)))
      .map((absence) => {
        const { from, to } = clipToMonth(absence, monthStart, monthEnd)
        return {
          absence,
          reason: absence.absenceReason as AbsenceReason,
          workingDays: countWorkingDays(from, to, holidayDates),
        }
      })
  }, [absences, holidays, monthStart, monthEnd])

  const totalsByReason = useMemo(() => {
    const totals = new Map<AbsenceReason, number>()
    entries.forEach(({ reason, workingDays }) =>
      totals.set(reason, (totals.get(reason) ?? 0) + workingDays)
    )
    return totals
  }, [entries])

  const totalDays = entries.reduce((sum, entry) => sum + entry.workingDays, 0)

  return (
    <div className={styles.container}>
      {loading ? (
        <div className={styles.spinnerContainer}>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <span className={styles.title}>Time off</span>
            <span className={styles.badge}>{formatDays(totalDays)}</span>
          </div>

          {entries.length === 0 ? (
            <span className={styles.empty}>No time off this month</span>
          ) : (
            <>
              <div className={styles.summary}>
                {Array.from(totalsByReason.entries()).map(([reason, days]) => (
                  <span className={styles.pill} key={reason}>
                    <span
                      className={styles.dot}
                      style={{ backgroundColor: TIME_OFF_REASONS[reason]?.color }}
                    />
                    {TIME_OFF_REASONS[reason]?.label} <b>{days}d</b>
                  </span>
                ))}
              </div>

              <ul className={styles.list}>
                {entries.map(({ absence, reason, workingDays }) => (
                  <li className={styles.item} key={absence.id}>
                    <span
                      className={styles.dot}
                      style={{ backgroundColor: TIME_OFF_REASONS[reason]?.color }}
                    />
                    <span className={styles.itemLabel}>{TIME_OFF_REASONS[reason]?.label}</span>
                    <span className={styles.itemRange}>{formatRange(absence)}</span>
                    <span className={styles.itemDays}>{formatDays(workingDays)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default TimeOff
